import React from 'react';

// DigitInput renders N (default 6) single-character boxes for entering a
// numeric verification code. Behaviour:
//   - typing a digit auto-advances focus to the next box
//   - Backspace on an empty box jumps back to the previous box
//   - Arrow keys navigate between boxes
//   - pasting a string of digits fills all boxes at once (and triggers
//     onComplete when the paste fills every slot)
//   - onComplete fires whenever the value reaches `length` digits, so
//     callers can auto-submit
//
// The component is uncontrolled-internally / controlled-externally: it
// reads from `value` and emits via `onChange`. Parents own the state so
// they can clear the field after a failed verify, prefill on retry, etc.

interface DigitInputProps {
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  length?: number;
  autoFocus?: boolean;
  disabled?: boolean;
}

const DigitInput: React.FC<DigitInputProps> = ({
  value,
  onChange,
  onComplete,
  length = 6,
  autoFocus = false,
  disabled = false,
}) => {
  const inputsRef = React.useRef<(HTMLInputElement | null)[]>([]);

  React.useEffect(() => {
    if (autoFocus) inputsRef.current[0]?.focus();
  }, [autoFocus]);

  // Always render `length` boxes; pad short values with empty strings.
  const digits: string[] = [];
  for (let i = 0; i < length; i++) digits.push(value[i] ?? '');

  const emit = (next: string) => {
    onChange(next);
    if (next.length === length && /^\d+$/.test(next)) {
      onComplete?.(next);
    }
  };

  const handleChange = (idx: number, raw: string) => {
    // Take just the last digit if browser autofill / IME pushed multiple
    // characters through a single onChange.
    const ch = raw.replace(/\D/g, '').slice(-1);
    if (!ch) return;
    const before = value.slice(0, idx);
    const after = value.slice(idx + 1);
    const next = (before + ch + after).slice(0, length);
    emit(next);
    if (idx < length - 1) inputsRef.current[idx + 1]?.focus();
  };

  const handleKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (digits[idx]) {
        // Clear THIS box's digit; stay focused so the user can retype.
        const next = value.slice(0, idx) + value.slice(idx + 1);
        onChange(next);
      } else if (idx > 0) {
        inputsRef.current[idx - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && idx > 0) {
      e.preventDefault();
      inputsRef.current[idx - 1]?.focus();
    } else if (e.key === 'ArrowRight' && idx < length - 1) {
      e.preventDefault();
      inputsRef.current[idx + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!text) return;
    emit(text);
    const nextIdx = Math.min(text.length, length - 1);
    inputsRef.current[nextIdx]?.focus();
  };

  return (
    <div className="flex gap-[8px] justify-center">
      {digits.map((d, idx) => (
        <input
          key={idx}
          ref={(el) => { inputsRef.current[idx] = el; }}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          value={d}
          disabled={disabled}
          onChange={(e) => handleChange(idx, e.target.value)}
          onKeyDown={(e) => handleKeyDown(idx, e)}
          onPaste={handlePaste}
          className="w-[40px] h-[48px] text-center text-[20px] font-bold border-b-2 border-[--Mid-Grey] focus:border-[--Primetime-main] outline-none bg-transparent disabled:opacity-50"
        />
      ))}
    </div>
  );
};

export default DigitInput;
